// Subscription Plan Types

export type PlanId = 'free' | 'starter' | 'professional' | 'business' | 'enterprise';
export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid' | 'incomplete' | 'grandfathered';
export type BillingCycle = 'monthly' | 'yearly';
export type InvoiceStatus = 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';

export interface SubscriptionPlan {
  id: PlanId;
  name: string;
  displayName: string;
  description: string | null;
  // Pricing (in EUR cents)
  monthlyPriceCents: number;
  yearlyPriceCents: number;
  // Stripe IDs
  stripeMonthlyPriceId: string | null;
  stripeYearlyPriceId: string | null;
  stripeProductId: string | null;
  // Quotas (null = unlimited)
  maxUsers: number | null;
  maxCustomers: number | null;
  maxAppointmentsPerMonth: number | null;
  maxLocations: number | null;
  monthlySmsIncluded: number;
  historyRetentionDays: number | null;
  // Feature flags
  hasEmailReminders: boolean;
  hasExcelExport: boolean;
  hasPdfExport: boolean;
  hasRecurringExpenses: boolean;
  hasAdvancedAnalytics: boolean;
  hasCustomBranding: boolean;
  hasCustomDomain: boolean;
  hasWhitelabel: boolean;
  hasApiAccess: boolean;
  hasMultiLocation: boolean;
  hasPrioritySupport: boolean;
  hasCustomEmailDomain: boolean;
  hasOnlineBooking: boolean;
  hasWaitingList: boolean;
  hasCustomerPortal: boolean;
  hasGoogleCalendar: boolean;
  hasInvoicing: boolean;
  hasDebtReminders: boolean;
  // Metadata
  sortOrder: number;
  isActive: boolean;
}

export interface Subscription {
  id: string;
  tenantId: string;
  planId: PlanId;
  status: SubscriptionStatus;
  billingCycle: BillingCycle;
  // Stripe IDs
  stripeSubscriptionId: string | null;
  stripeCustomerId: string | null;
  // Dates
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  trialEndsAt: string | null;
  canceledAt: string | null;
  // Grandfathering
  isGrandfathered: boolean;
  grandfatheredAt: string | null;
  // Metadata
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface UsageTracking {
  id: string;
  tenantId: string;
  period: string; // YYYY-MM format
  appointmentsCount: number;
  smsSentCount: number;
  customersCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface SmsPackage {
  id: string;
  name: string;
  smsCount: number;
  priceCents: number;
  pricePerSmsCents: number;
  stripePriceId: string | null;
  stripeProductId: string | null;
  sortOrder: number;
  isActive: boolean;
}

export interface TenantSmsCredits {
  id: string;
  tenantId: string;
  creditsRemaining: number;
  creditsUsed: number;
  planCreditsRemaining: number;
  planCreditsResetAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Invoice {
  id: string;
  tenantId: string;
  // Stripe IDs
  stripeInvoiceId: string | null;
  stripeChargeId: string | null;
  stripePaymentIntentId: string | null;
  // Invoice details
  invoiceNumber: string | null;
  description: string | null;
  // Amount (in EUR cents)
  amountCents: number;
  taxCents: number;
  totalCents: number;
  currency: string;
  // Status
  status: InvoiceStatus;
  // Related items
  subscriptionId: string | null;
  smsPackageId: string | null;
  // URLs
  invoicePdfUrl: string | null;
  hostedInvoiceUrl: string | null;
  // Dates
  invoiceDate: string;
  dueDate: string | null;
  paidAt: string | null;
  // Metadata
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

// Feature keys for feature gating
export type FeatureKey =
  | 'email_reminders'
  | 'excel_export'
  | 'pdf_export'
  | 'recurring_expenses'
  | 'advanced_analytics'
  | 'custom_branding'
  | 'custom_domain'
  | 'custom_email_domain'
  | 'whitelabel'
  | 'api_access'
  | 'multi_location'
  | 'priority_support'
  | 'online_booking'
  | 'waiting_list'
  | 'customer_portal'
  | 'google_calendar'
  | 'invoicing'
  | 'debt_reminders';

// Quota keys for usage limits
export type QuotaKey = 'users' | 'customers' | 'appointments' | 'sms';

// Quota check result
export interface QuotaCheckResult {
  allowed: boolean;
  current: number;
  limit: number | null;
  quotaType: QuotaKey;
  planId: PlanId;
}

// Combined subscription state for UI
export interface SubscriptionState {
  plan: SubscriptionPlan | null;
  subscription: Subscription | null;
  usage: UsageTracking | null;
  smsCredits: TenantSmsCredits | null;
}

// Database row types (snake_case for Supabase)
export interface SubscriptionPlanRow {
  id: string;
  name: string;
  display_name: string;
  description: string | null;
  monthly_price_cents: number;
  yearly_price_cents: number;
  stripe_monthly_price_id: string | null;
  stripe_yearly_price_id: string | null;
  stripe_product_id: string | null;
  max_users: number | null;
  max_customers: number | null;
  max_appointments_per_month: number | null;
  max_locations: number | null;
  monthly_sms_included: number;
  history_retention_days: number | null;
  has_email_reminders: boolean;
  has_excel_export: boolean;
  has_pdf_export: boolean;
  has_recurring_expenses: boolean;
  has_advanced_analytics: boolean;
  has_custom_branding: boolean;
  has_custom_domain: boolean;
  has_whitelabel: boolean;
  has_api_access: boolean;
  has_multi_location: boolean;
  has_priority_support: boolean;
  has_custom_email_domain: boolean;
  has_online_booking: boolean;
  has_waiting_list: boolean;
  has_customer_portal: boolean;
  has_google_calendar: boolean;
  has_invoicing: boolean;
  has_debt_reminders: boolean;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SubscriptionRow {
  id: string;
  tenant_id: string;
  plan_id: string;
  status: string;
  billing_cycle: string;
  stripe_subscription_id: string | null;
  stripe_customer_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  trial_ends_at: string | null;
  canceled_at: string | null;
  is_grandfathered: boolean;
  grandfathered_at: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface UsageTrackingRow {
  id: string;
  tenant_id: string;
  period: string;
  appointments_count: number;
  sms_sent_count: number;
  customers_count: number;
  created_at: string;
  updated_at: string;
}

export interface SmsPackageRow {
  id: string;
  name: string;
  sms_count: number;
  price_cents: number;
  price_per_sms_cents: number;
  stripe_price_id: string | null;
  stripe_product_id: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
}

export interface TenantSmsCreditsRow {
  id: string;
  tenant_id: string;
  credits_remaining: number;
  credits_used: number;
  plan_credits_remaining: number;
  plan_credits_reset_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface InvoiceRow {
  id: string;
  tenant_id: string;
  stripe_invoice_id: string | null;
  stripe_charge_id: string | null;
  stripe_payment_intent_id: string | null;
  invoice_number: string | null;
  description: string | null;
  amount_cents: number;
  tax_cents: number;
  total_cents: number;
  currency: string;
  status: string;
  subscription_id: string | null;
  sms_package_id: string | null;
  invoice_pdf_url: string | null;
  hosted_invoice_url: string | null;
  invoice_date: string;
  due_date: string | null;
  paid_at: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

// Helper functions to convert between row and client types
export function subscriptionPlanFromRow(row: SubscriptionPlanRow): SubscriptionPlan {
  return {
    id: row.id as PlanId,
    name: row.name,
    displayName: row.display_name,
    description: row.description,
    monthlyPriceCents: row.monthly_price_cents,
    yearlyPriceCents: row.yearly_price_cents,
    stripeMonthlyPriceId: row.stripe_monthly_price_id,
    stripeYearlyPriceId: row.stripe_yearly_price_id,
    stripeProductId: row.stripe_product_id,
    maxUsers: row.max_users,
    maxCustomers: row.max_customers,
    maxAppointmentsPerMonth: row.max_appointments_per_month,
    maxLocations: row.max_locations,
    monthlySmsIncluded: row.monthly_sms_included,
    historyRetentionDays: row.history_retention_days,
    hasEmailReminders: row.has_email_reminders,
    hasExcelExport: row.has_excel_export,
    hasPdfExport: row.has_pdf_export,
    hasRecurringExpenses: row.has_recurring_expenses,
    hasAdvancedAnalytics: row.has_advanced_analytics,
    hasCustomBranding: row.has_custom_branding,
    hasCustomDomain: row.has_custom_domain,
    hasWhitelabel: row.has_whitelabel,
    hasApiAccess: row.has_api_access,
    hasMultiLocation: row.has_multi_location,
    hasPrioritySupport: row.has_priority_support,
    hasCustomEmailDomain: row.has_custom_email_domain,
    hasOnlineBooking: row.has_online_booking,
    hasWaitingList: row.has_waiting_list ?? false,
    hasCustomerPortal: row.has_customer_portal ?? false,
    hasGoogleCalendar: row.has_google_calendar,
    hasInvoicing: row.has_invoicing ?? false,
    hasDebtReminders: row.has_debt_reminders ?? false,
    sortOrder: row.sort_order,
    isActive: row.is_active,
  };
}

export function subscriptionFromRow(row: SubscriptionRow): Subscription {
  return {
    id: row.id,
    tenantId: row.tenant_id,
    planId: row.plan_id as PlanId,
    status: row.status as SubscriptionStatus,
    billingCycle: row.billing_cycle as BillingCycle,
    stripeSubscriptionId: row.stripe_subscription_id,
    stripeCustomerId: row.stripe_customer_id,
    currentPeriodStart: row.current_period_start,
    currentPeriodEnd: row.current_period_end,
    trialEndsAt: row.trial_ends_at,
    canceledAt: row.canceled_at,
    isGrandfathered: row.is_grandfathered,
    grandfatheredAt: row.grandfathered_at,
    metadata: row.metadata,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function usageTrackingFromRow(row: UsageTrackingRow): UsageTracking {
  return {
    id: row.id,
    tenantId: row.tenant_id,
    period: row.period,
    appointmentsCount: row.appointments_count,
    smsSentCount: row.sms_sent_count,
    customersCount: row.customers_count,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function smsPackageFromRow(row: SmsPackageRow): SmsPackage {
  return {
    id: row.id,
    name: row.name,
    smsCount: row.sms_count,
    priceCents: row.price_cents,
    pricePerSmsCents: row.price_per_sms_cents,
    stripePriceId: row.stripe_price_id,
    stripeProductId: row.stripe_product_id,
    sortOrder: row.sort_order,
    isActive: row.is_active,
  };
}

export function tenantSmsCreditsFromRow(row: TenantSmsCreditsRow): TenantSmsCredits {
  return {
    id: row.id,
    tenantId: row.tenant_id,
    creditsRemaining: row.credits_remaining,
    creditsUsed: row.credits_used,
    planCreditsRemaining: row.plan_credits_remaining,
    planCreditsResetAt: row.plan_credits_reset_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function invoiceFromRow(row: InvoiceRow): Invoice {
  return {
    id: row.id,
    tenantId: row.tenant_id,
    stripeInvoiceId: row.stripe_invoice_id,
    stripeChargeId: row.stripe_charge_id,
    stripePaymentIntentId: row.stripe_payment_intent_id,
    invoiceNumber: row.invoice_number,
    description: row.description,
    amountCents: row.amount_cents,
    taxCents: row.tax_cents,
    totalCents: row.total_cents,
    currency: row.currency,
    status: row.status as InvoiceStatus,
    subscriptionId: row.subscription_id,
    smsPackageId: row.sms_package_id,
    invoicePdfUrl: row.invoice_pdf_url,
    hostedInvoiceUrl: row.hosted_invoice_url,
    invoiceDate: row.invoice_date,
    dueDate: row.due_date,
    paidAt: row.paid_at,
    metadata: row.metadata,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}
